import { STANDARD_SHIPPING_ONLY_PRODUCT_TAGS } from "../constants/shipping.constants";
import { toProductVariantGid } from "../services/product.service";
import { storefrontApiRequest } from "../services/shopify.service";
import { renderProductColorDisplayValue } from "./template-renderers";
export async function fetchAdditionalProductVariantInfo(variantIds, options) {
    if (!variantIds.length) {
        return {};
    }
    const gids = variantIds.map(toProductVariantGid);
    const query = `
    query getProductVariants($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          selectedOptions {
            name
            value
          }
          standardShippingOnly: metafield(namespace: "custom", key: "standard_shipping_only") {
            value
          }
          product {
            id
            compareAtPriceRange {
                minVariantPrice {
                amount
              }
            }
            tags
          }
        }
      }
    }
  `;
    const res = await storefrontApiRequest(query, { ids: gids });
    if (!res?.data?.nodes) {
        console.error("Failed to fetch additional product variant info");
        return {};
    }
    const output = res.data.nodes.filter(n => !!n).reduce((acc, curr) => {
        const idArr = curr.id.split("/");
        const key = idArr[idArr.length - 1];
        const colorDisplayValue = renderProductColorDisplayValue(curr.product);
        let badgingText = null;
        const badgeTag = curr.product.tags.find(t => t.startsWith(options.badgePrefix));
        const badgeTextArr = badgeTag ? badgeTag.split(":") : null;
        if (badgeTextArr && window.UNTUCKitConfig?.showProductBadging) {
            badgingText = badgeTextArr[badgeTextArr.length - 1]; // sometimes the ecomm team uses :: (e.g. badge-pdp::)
        }
        const compareAtPrice = +curr.product.compareAtPriceRange.minVariantPrice.amount * 100;
        acc[key] = {
            availableForSale: curr.availableForSale,
            colorDisplayValue: colorDisplayValue,
            compareAtPrice: compareAtPrice,
            badgingText: badgingText,
            selectedOptions: curr.selectedOptions,
            standardShippingOnly: isStandardShippingOnly(parseStandardShippingOnlyMetafield(curr.standardShippingOnly?.value), curr.product.tags),
        };
        return acc;
    }, {});
    return output;
}
export function hasStandardShippingOnlyLineItems(items) {
    return items.some((item) => item.standardShippingOnly);
}
export function createStandardShippingOnlyOrderMessage() {
    const el = document.createElement('p');
    el.className = 'cart__standard-shipping-only-message';
    el.textContent = window.cartUI.standardShippingOnly.orderMessage;
    return el;
}
export async function renderCartContents(cartData) {
    const variantIds = cartData.items.map((i) => i.variant_id);
    const additionalData = await fetchAdditionalProductVariantInfo(variantIds, { badgePrefix: "badge-cart:" });
    const cartLineItemDataExtended = cartData.items.map((i) => {
        const extraData = additionalData[i.variant_id];
        return extraData ? { ...i, ...extraData } : i;
    });
    document.querySelectorAll('.js-cart-contents').forEach((el) => {
        el.innerHTML = '';
        if (hasStandardShippingOnlyLineItems(cartLineItemDataExtended)) {
            el.appendChild(createStandardShippingOnlyOrderMessage());
        }
        const cartItemEls = cartLineItemDataExtended.map((item, idx) => {
            return createCartLineItem(item, { lineNumber: idx + 1, actionsType: 'inline-cart', styleClass: 'cart-line-item--listed' });
        });
        el.append(...cartItemEls);
    });
    return cartLineItemDataExtended;
}
function parseStandardShippingOnlyMetafield(value) {
    if (!value) {
        return false;
    }
    try {
        return !!JSON.parse(value);
    }
    catch {
        return value === 'true';
    }
}
function isStandardShippingOnlyProduct(tags) {
    return tags.some((tag) => {
        const lowerTag = tag.toLowerCase();
        return STANDARD_SHIPPING_ONLY_PRODUCT_TAGS.some((standardTag) => lowerTag.includes(standardTag));
    });
}
function isStandardShippingOnly(variantMetafieldValue, productTags) {
    return variantMetafieldValue || isStandardShippingOnlyProduct(productTags);
}
export function createCartLineItem(cartLineItem, config) {
    const el = document.createElement('cart-line-item');
    el.setAttribute('cart-line-item-data', JSON.stringify(cartLineItem));
    if (config.showQuantity !== undefined) {
        el.setAttribute('show-quantity', `${config.showQuantity}`);
    }
    if (config.lineNumber) {
        el.setAttribute("line-number", `${config.lineNumber}`);
    }
    el.setAttribute('actions-type', config.actionsType);
    let badgingText = cartLineItem.badgingText || '';
    // override badgingText in cart if discount code has been applied
    // this avoids awkward messaging if the discount code has already been applied (e.g. "30% off with code XYZ")
    if (['inline-cart', 'add-to-cart-overlay'].includes(config.actionsType) && cartLineItem.line_level_discount_allocations?.length) {
        const discountCodeAppliedMessaging = cartLineItem.line_level_discount_allocations
            .filter(llda => llda.amount > 0 && llda.discount_application.type === 'discount_code') // discounts can be 0 when applied via script editor
            // TODO: figure out how to handle amount off/percentage off
            .map(llda => {
            const da = llda.discount_application;
            switch (da.type) {
                case 'discount_code':
                    return `${da.title} Applied`;
                default:
                    return da.title;
            }
        }).join('<br/>');
        if (discountCodeAppliedMessaging) {
            badgingText = discountCodeAppliedMessaging;
        }
    }
    el.setAttribute('badge-text', badgingText);
    el.setAttribute('compare-at-price', `${cartLineItem.compareAtPrice}`);
    el.setAttribute("color-display-value", cartLineItem.colorDisplayValue || '');
    if (cartLineItem.availableForSale !== undefined) {
        el.setAttribute("is-available", `${cartLineItem.availableForSale}`);
    }
    if (cartLineItem.standardShippingOnly) {
        el.setAttribute('standard-shipping-only', 'true');
    }
    el.classList.add(config.styleClass);
    return el;
}
